// Wizard Cache Utility - Phase 2 of 3-phase persistence solution
// Provides instant UI restoration without waiting for database
// EPOCH 59: Added messages and sequenceName to prevent data loss on tab switch

// Message structure matching CampaignCreationState
export interface CachedMessage {
  message_number: number;
  content: string;
  character_count: number;
  delay_days: number;
}

export interface WizardCache {
  campaignId: string;
  step: number;
  leadMethod: string | null;
  campaignName?: string;
  serviceTier?: string;
  // EPOCH 59: Added messages and sequenceName to cache
  messages?: CachedMessage[];
  sequenceName?: string;
  // Epoch 80: campaign goal
  campaignGoal?: 'product_discovery' | 'lead_generation';
  timestamp: number;
}

export const WizardCache = {
  write(campaignId: string, data: Partial<WizardCache>) {
    if (!campaignId) return;
    
    const key = `wizard_${campaignId}`;
    const existing = this.read(campaignId) || {} as WizardCache;
    const updated: WizardCache = {
      ...existing,
      ...data,
      campaignId,
      timestamp: Date.now()
    };
    
    try {
      sessionStorage.setItem(key, JSON.stringify(updated));
      console.log('📝 Cache written:', {
        campaignId,
        step: updated.step,
        leadMethod: updated.leadMethod,
        serviceTier: updated.serviceTier,
        // EPOCH 59: Log messages count for debugging
        messagesCount: updated.messages?.length || 0,
        sequenceName: updated.sequenceName || '(not set)'
      });
    } catch (error) {
      console.error('Failed to write wizard cache:', error);
    }
  },

  read(campaignId: string): WizardCache | null {
    if (!campaignId) return null;
    
    const key = `wizard_${campaignId}`;
    try {
      const stored = sessionStorage.getItem(key);
      if (!stored) return null;
      
      const data = JSON.parse(stored) as WizardCache;
      
      // Expire after 2 hours
      if (Date.now() - data.timestamp > 7200000) {
        this.clear(campaignId);
        return null;
      }
      
      console.log('📖 Cache read:', {
        campaignId,
        step: data.step,
        leadMethod: data.leadMethod,
        // EPOCH 59: Log messages count for debugging
        messagesCount: data.messages?.length || 0,
        sequenceName: data.sequenceName || '(not set)',
        age: Math.round((Date.now() - data.timestamp) / 1000) + 's'
      });
      
      return data;
    } catch (error) {
      console.error('Failed to read wizard cache:', error);
      return null;
    }
  },

  clear(campaignId: string) {
    if (!campaignId) return;
    
    const key = `wizard_${campaignId}`;
    try {
      sessionStorage.removeItem(key);
      console.log('🗑️ Cache cleared for:', campaignId);
    } catch (error) {
      console.error('Failed to clear wizard cache:', error);
    }
  },

  // Helper to check if cache is fresh (less than 5 minutes old)
  isFresh(campaignId: string): boolean {
    const cache = this.read(campaignId);
    if (!cache) return false;
    
    const age = Date.now() - cache.timestamp;
    return age < 300000; // 5 minutes
  }
};

// Reconciliation helper - decides whether to use cache or server data
// EPOCH 59: Added messages and sequence_name to return type
export function reconcileWizardState(
  serverData: any | null,
  cacheData: WizardCache | null
): {
  lead_acquisition_method: string | null;
  current_step?: number;
  campaign_name?: string;
  service_tier?: string;
  // EPOCH 59: Added messages and sequence_name
  messages?: CachedMessage[];
  sequence_name?: string;
  // Epoch 80
  campaignGoal?: 'product_discovery' | 'lead_generation';
} {
  // No data at all - return defaults
  if (!serverData && !cacheData) {
    return { lead_acquisition_method: null };
  }

  // Only cache exists - use it optimistically
  // EPOCH 59: Fixed logic bug (!cacheData && cacheData was always false)
  if (cacheData && !serverData) {
    return {
      lead_acquisition_method: cacheData.leadMethod,
      current_step: cacheData.step,
      campaign_name: cacheData.campaignName,
      service_tier: cacheData.serviceTier,
      // EPOCH 59: Include messages from cache
      messages: cacheData.messages,
      sequence_name: cacheData.sequenceName,
      campaignGoal: cacheData.campaignGoal
    };
  }

  // Only server exists - use it
  if (serverData && !cacheData) {
    return {
      lead_acquisition_method: serverData.target_audience_type || null,
      campaign_name: serverData.name,
      service_tier: serverData.service_tier,
      campaignGoal: serverData.campaign_goal
      // Note: messages will be loaded separately via loadSequenceData
    };
  }

  // Both exist - prefer cache if it's newer than server
  if (serverData && cacheData) {
    const serverTime = new Date(serverData.updated_at).getTime();
    const cacheTime = cacheData.timestamp;

    // Cache is newer and has the critical field
    if (cacheTime > serverTime && cacheData.leadMethod) {
      console.log('📊 Using cache data (newer than server)', {
        messagesFromCache: cacheData.messages?.length || 0
      });
      return {
        lead_acquisition_method: cacheData.leadMethod,
        current_step: cacheData.step,
        campaign_name: cacheData.campaignName || serverData.name,
        service_tier: cacheData.serviceTier || serverData.service_tier,
        // EPOCH 59: Include messages from cache when cache is newer
        messages: cacheData.messages,
        sequence_name: cacheData.sequenceName,
        campaignGoal: cacheData.campaignGoal || serverData.campaign_goal
      };
    }

    // Server is newer or cache missing critical field
    console.log('📊 Using server data (newer than cache)');
    return {
      lead_acquisition_method: serverData.target_audience_type || null,
      campaign_name: serverData.name,
      service_tier: serverData.service_tier,
      campaignGoal: serverData.campaign_goal
      // Note: messages will be loaded separately via loadSequenceData
    };
  }

  // Fallback
  return { lead_acquisition_method: null };
}